import type { AoSGameState, Player, BarbadosPlayer } from '../../types';
import { debug } from '../../utils/debug';

/**
 * ゲーム終了処理（Barbados専用）
 *
 * 10ターン終了時または倒産時に呼び出され、株式買い戻しと勝敗判定を行います。
 *
 * 参照:
 * - docs/specifications/logic/barbados/game_end_override.md
 *
 * @param G ゲーム状態
 * @param ctx Boardgame.ioコンテキスト
 * @param events Boardgame.ioイベント
 */
export function endGameBarbados(
  G: AoSGameState,
  ctx: any,
  events: any
): void {
  const player = G.players[ctx.playOrder[0]] as BarbadosPlayer;

  debug.log(`[endGameBarbados] Game ending - Turn: ${ctx.turn}, Bankrupt: ${player.isBankrupt}`);

  // 1. 倒産チェック
  if (player.isBankrupt) {
    // 倒産による敗北
    G.isGameOver = true;
    G.endReason = 'BANKRUPTCY';

    // プレイヤー情報更新（仕様: player.finalCash, player.isVictorious）
    player.finalCash = player.cash;
    player.isVictorious = false;

    // スコア記録
    G.finalScores[player.id] = player.cash;

    debug.log(`[endGameBarbados] Player bankrupt - Final cash: $${player.cash}`);

    // Boardgame.ioにゲーム終了を通知
    if (events && events.endGame) {
      events.endGame({
        completed: false,
        reason: 'BANKRUPTCY',
        finalCash: player.cash,
        victory: false,
        sharesIssued: player.sharesIssued
      });
    }
    return;
  }

  // 2. 株式買い戻し処理
  const buybackCost = player.sharesIssued * 5;
  const finalCash = player.cash - buybackCost;

  // 3. 勝敗判定
  const victory = finalCash >= 0;

  // 4. プレイヤー情報更新（仕様: player.finalCash, player.isVictorious）
  player.finalCash = finalCash;
  player.isVictorious = victory;

  // 5. スコア記録
  G.finalScores[player.id] = finalCash;

  // 6. ゲーム状態更新（仕様: VICTORY/DEFEAT）
  G.isGameOver = true;
  G.endReason = victory ? 'VICTORY' : 'DEFEAT';

  debug.log(`[endGameBarbados] Game completed - Cash: $${player.cash}, Buyback: $${buybackCost}, Final: $${finalCash}, Victory: ${victory}`);

  // 7. Boardgame.ioにゲーム終了を通知
  if (events && events.endGame) {
    events.endGame({
      completed: true,
      finalCash,
      victory,
      buybackCost,
      sharesIssued: player.sharesIssued
    });
  }
}

/**
 * 株式買い戻しコストを計算
 *
 * @param player プレイヤー
 * @returns 買い戻しコスト
 */
export function calculateBuybackCost(player: Player): number {
  return player.sharesIssued * 5;
}

/**
 * 勝利判定
 *
 * @param player プレイヤー
 * @returns 勝利ならtrue、敗北ならfalse
 */
export function isVictorious(player: Player): boolean {
  const buybackCost = calculateBuybackCost(player);
  const finalCash = player.cash - buybackCost;
  return finalCash >= 0;
}
